// src/prototype/enterprise/evolution/evolutionModel.ts
// ============================================================
// ENTERPRISE · INITIATIVE EVOLUTION (Surface "1i") — the shared A∩C view model.
//
// For one initiative, assembles its trajectory across the 3 runs (composite + confidence +
// rank), its current dimension scores, its decision history, and decision markers positioned
// on the chart timeline. All read-only from the spine (getRuns / getPortfolioAsOf /
// getDecisionLog). Chart geometry mirrors Enterprise Prototype.dc.html · evolution view.
// ============================================================

import { getRuns, getPortfolioAsOf, getDecisionLog } from '../../data/adapters/enterpriseSpine';
import { statusByUseCase, initiativeName, type DisplayStatus } from '../portfolio/portfolioDisplay';
import { DECISION_DOT, type DecisionKind } from '../decisions/decisionsModel';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDate = (iso: string) => `${MONTHS[+iso.slice(5, 7) - 1]} ${+iso.slice(8, 10)}`;

// Chart geometry (viewBox 0 0 912 170), matching the design's 3-run x positions + scales.
export const EVO_X = [130, 468, 806];
const clampY = (y: number) => Math.max(28, Math.min(150, y));
const yComp = (comp: number) => clampY(150 - (comp - 48) * (120 / 32));
const yConf = (conf: number) => clampY(150 - (conf - 35) * (120 / 60));

export interface EvoRun {
  label: string;
  dateLabel: string;
  rank: number;
  comp: number;
  conf: number; // %
}
export interface EvoDim {
  name: string;
  val: number;
  accent: boolean; // the design accents Implementation Feasibility in orange
}
export interface EvoDecisionItem {
  decision: DecisionKind;
  date: string;
  by: string;
  rationale: string;
  color: string;
}
export interface EvoMarker {
  x: number;
  y: number;
  decision: DecisionKind;
  dateLabel: string;
  color: string;
  label: string; // e.g. "APPROVED · Apr 22"
}

export interface Evolution {
  found: boolean;
  useCaseId: number;
  title: string;
  owner: { name: string; org: string };
  category: string;
  currentRank: number;
  currentComp: number;
  currentConf: number;
  currentStatus: DisplayStatus;
  trendLabel: string;
  trendColor: string;
  runs: EvoRun[];
  compPts: string; // svg polyline points
  confPts: string;
  compDots: { x: number; y: number; val: number }[];
  currentRunLabel: string;
  dims: EvoDim[];
  decisions: EvoDecisionItem[];
  markers: EvoMarker[];
}

/** Two-step display attribution for the hero's (uc 1555) Q2 approval (see decisionsModel FIX 2). */
function decisionBy(d: { use_case_id: number; decision: string; scenario_id?: string; decided_by: { name: string; role: string } }): string {
  if (d.use_case_id === 1555 && d.decision === 'APPROVE' && d.scenario_id) {
    return `${d.decided_by.name} · CFO gate → committee ratified`;
  }
  return `${d.decided_by.name} · ${d.decided_by.role}`;
}

export function buildEvolution(useCaseId: number): Evolution {
  const runs = getRuns(); // oldest → newest (Q1, Q2, +6mo)
  const perRun = runs.map((r) => {
    const asof = getPortfolioAsOf(r.run_id);
    const it = asof?.initiatives.find((i) => i.use_case_id === useCaseId) ?? null;
    return { run_id: r.run_id, label: r.label, created_at: r.created_at, it };
  });

  const anyIt = perRun.find((p) => p.it)?.it;
  const latest = [...perRun].reverse().find((p) => p.it)?.it ?? anyIt ?? null;
  if (!anyIt || !latest) {
    return {
      found: false, useCaseId, title: `Use case ${useCaseId}`, owner: { name: '—', org: '—' }, category: '—',
      currentRank: 0, currentComp: 0, currentConf: 0, currentStatus: 'Active',
      trendLabel: '', trendColor: '#717182', runs: [], compPts: '', confPts: '', compDots: [],
      currentRunLabel: '', dims: [], decisions: [], markers: [],
    };
  }

  const evoRuns: EvoRun[] = perRun.map((p) => ({
    label: p.label,
    dateLabel: fmtDate(p.created_at),
    rank: p.it?.rank ?? 0,
    comp: p.it?.composite ?? 0,
    conf: Math.round((p.it?.confidence ?? 0) * 100),
  }));

  const compPts = evoRuns.map((r, i) => `${EVO_X[i]},${yComp(r.comp).toFixed(0)}`).join(' ');
  const confPts = evoRuns.map((r, i) => `${EVO_X[i]},${yConf(r.conf).toFixed(0)}`).join(' ');
  const compDots = evoRuns.map((r, i) => ({ x: EVO_X[i], y: yComp(r.comp), val: r.comp }));

  const firstComp = evoRuns[0]?.comp ?? latest.composite;
  const delta = Math.round((latest.composite - firstComp) * 10) / 10;
  const trendLabel = `${delta >= 0 ? '▲ +' : '▼ −'}${Math.abs(delta).toFixed(1)} since Q1`;
  const trendColor = delta >= 0 ? '#15803d' : '#b91c1c';

  const d = latest.dimension_scores;
  const dims: EvoDim[] = [
    { name: 'Financial Impact', val: Math.round(d.financial_impact), accent: false },
    { name: 'Operational Urgency', val: Math.round(d.operational_urgency), accent: false },
    { name: 'Strategic Alignment', val: Math.round(d.strategic_alignment), accent: false },
    { name: 'Implementation Feasibility', val: Math.round(d.implementation_feasibility), accent: true },
  ];

  const runIndex = new Map(runs.map((r, i) => [r.run_id, i]));
  const log = getDecisionLog({ use_case_id: useCaseId }); // newest-first
  const decisions: EvoDecisionItem[] = log.map((entry) => ({
    decision: entry.decision,
    date: fmtDate(entry.decided_at),
    by: decisionBy(entry),
    rationale: entry.rationale,
    color: DECISION_DOT[entry.decision],
  }));

  const markers: EvoMarker[] = log
    .filter((entry) => entry.run_id != null && runIndex.has(entry.run_id))
    .map((entry) => {
      const idx = runIndex.get(entry.run_id!)!;
      return {
        x: EVO_X[idx],
        y: yComp(evoRuns[idx].comp),
        decision: entry.decision,
        dateLabel: fmtDate(entry.decided_at),
        color: DECISION_DOT[entry.decision],
        label: `${entry.decision} · ${fmtDate(entry.decided_at)}`,
      };
    });

  const currentStatus = statusByUseCase(getDecisionLog()).get(useCaseId) ?? 'Active';

  return {
    found: true,
    useCaseId,
    title: initiativeName(latest.title),
    owner: latest.owner,
    category: latest.category,
    currentRank: latest.rank,
    currentComp: latest.composite,
    currentConf: Math.round(latest.confidence * 100),
    currentStatus,
    trendLabel,
    trendColor,
    runs: evoRuns,
    compPts,
    confPts,
    compDots,
    currentRunLabel: perRun[perRun.length - 1].label,
    dims,
    decisions,
    markers,
  };
}
